import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileSpreadsheet, PlusCircle, HelpCircle, KeyRound, Users,
  GraduationCap, ScanLine, QrCode, Trophy, BarChart3, TrendingUp, LineChart,
  MessageSquareShare, Printer, Settings as SettingsIcon, Menu, X, Moon, Sun, LogOut, Building2, Clock, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PALETTES, ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const GROUPS = [
  {
    title: "Ana Modüller",
    items: [
      { to: "/", label: "Genel Bakış", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
      { to: "/sinavlar", label: "Sınavlar", icon: FileSpreadsheet, tid: "nav-exams" },
      { to: "/sinav-olustur", label: "Sınav Oluştur", icon: PlusCircle, tid: "nav-create-exam" },
      { to: "/ogrenciler", label: "Öğrenciler", icon: Users, tid: "nav-students" },
      { to: "/siniflar", label: "Sınıflar & Şubeler", icon: GraduationCap, tid: "nav-classes" },
    ],
  },
  {
    title: "Sınav & Optik",
    items: [
      { to: "/optik-okuma", label: "Optik Okuma", icon: QrCode, tid: "nav-optical-read" },
      { to: "/sinif-analizi", label: "Sınıf Analizi", icon: BarChart3, tid: "nav-class-analytics" },
      { to: "/ogrenci-analizi", label: "Öğrenci Analizi", icon: TrendingUp, tid: "nav-student-analytics" },
      { to: "/analiz", label: "Sınav Analizi", icon: LineChart, tid: "nav-ktt-analytics" },
    ],
  },
  {
    title: "İletişim & Sistem",
    items: [
      { to: "/veli-bildirimleri", label: "Veli Bildirimleri", icon: MessageSquareShare, tid: "nav-parent" },
      { to: "/raporlar", label: "Raporlar & Karneler", icon: Printer, tid: "nav-reports" },
      { to: "/kilavuz", label: "Kullanım Kılavuzu", icon: BookOpen, tid: "nav-guide" },
      { to: "/ayarlar", label: "Ayarlar", icon: SettingsIcon, tid: "nav-settings" },
      { to: "/kurumlar", label: "Kurumlar (Süper Yönetici)", icon: Building2, tid: "nav-orgs", superOnly: true },
    ],
  },
];

export default function Layout() {
  const { user, logout, org } = useAuth();
  const { dark, setDark, palette, setPalette } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data.theme_palette) setPalette(r.data.theme_palette);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const doLogout = () => { logout(); nav("/giris"); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={`no-print fixed inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col app-safe-top transform transition-transform duration-300 md:translate-x-0 ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
          <img src="/logo192.png" alt="Logo" className="h-10 w-10 rounded-lg object-cover" />
          <div className="leading-tight">
            <div className="font-bold tracking-tight text-sm">HAFTALIK KTT</div>
            <div className="text-[11px] text-muted-foreground truncate max-w-[160px]">{settings?.institution_name || "Ölçme Değerlendirme"}</div>
          </div>
          <button className="ml-auto md:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto ktt-scroll py-4 px-3 space-y-5">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</div>
              <div className="space-y-0.5">
                {g.items.filter((it) => !it.superOnly || user?.platform_role === "superadmin").map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`${it.tid}-link`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                      }`
                    }
                  >
                    <it.icon className="h-[18px] w-[18px]" />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-border text-[11px] text-muted-foreground">
          {settings?.academic_year} Eğitim Öğretim Yılı
        </div>
      </aside>

      {open && <div className="no-print fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="md:pl-72 flex flex-col min-h-screen">
        <header className="no-print sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur-md px-4 sm:px-6 flex items-center gap-3 app-safe-top min-h-16 h-16" style={{ boxSizing: "content-box" }}>
          <button className="md:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu className="h-6 w-6" /></button>
          <div className="hidden sm:block text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
            {settings?.academic_year} · 1. Dönem
          </div>
          {org?.status === "trial" && (
            <div className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${org.days_left <= 2 ? "bg-rose-500/15 text-rose-700 dark:text-rose-300" : "bg-sky-500/15 text-sky-700 dark:text-sky-300"}`} data-testid="trial-badge">
              <Clock className="h-3.5 w-3.5" />Deneme: {org.days_left} gün kaldı
            </div>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="palette-trigger">
                  <span className="h-3.5 w-3.5 rounded-full" style={{ background: PALETTES.find((p) => p.id === palette)?.color }} />
                  <span className="hidden sm:inline">Tema Rengi</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border">
                {PALETTES.map((p) => (
                  <DropdownMenuItem key={p.id} onClick={() => setPalette(p.id)} data-testid={`palette-${p.id}`} className="gap-2">
                    <span className="h-3.5 w-3.5 rounded-full" style={{ background: p.color }} />{p.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2" data-testid="profile-trigger">
                  <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-sm font-bold">
                    {(user?.name || "?")[0]?.toUpperCase()}
                  </span>
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-sm font-semibold">{user?.name}</span>
                    <span className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</span>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-destructive gap-2">
                  <LogOut className="h-4 w-4" /> Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
